import React, { useEffect, useState, useRef } from "react";
import {
  MapContainer,
  TileLayer,
  Marker,
  Tooltip as LeafletTooltip,
  useMapEvents,
} from "react-leaflet";
import MarkerClusterGroup from "@changey/react-leaflet-markercluster";
import L from "leaflet";
import { Spin, Button, Tooltip as AntdTooltip } from "antd";
import Preview from "../../pages/planning/VisitBoard/TableView/modals/Preview";
import "leaflet.heat";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import {
  ExportOutlined,
  FileImageOutlined,
  FilePdfOutlined,
  HeatMapOutlined,
  SwapOutlined,
} from "@ant-design/icons";

import CryptoJS from "crypto-js";
import "./MapComponent.scss";

// Utility: generate color based on projectId
const generateColorForId = (projectId) => {
  const hash = CryptoJS.MD5(projectId).toString();
  const r = parseInt(hash.substring(0, 2), 16);
  const g = parseInt(hash.substring(2, 4), 16);
  const b = parseInt(hash.substring(4, 6), 16);
  return `rgb(${r}, ${g}, ${b})`;
};

// Custom SVG Icon Generator
const createCustomIcon = (projectId, iconHtml = "") => {
  const color = generateColorForId(projectId);
  const svgIcon = `
    <div style="position: relative; width: 40px; height: 60px;">
      <div style="position: absolute; top: 0; left: 0; z-index: 1;">
        <svg width="40" height="60" viewBox="0 0 40 60" xmlns="http://www.w3.org/2000/svg">
          <path d="M20 0C9 0 0 9 0 20c0 11 20 40 20 40s20-29 20-40C40 9 31 0 20 0z" fill="${color}" />
        </svg>
      </div>
      <div style="position: absolute; top: 5px; left: 5px; z-index: 2; width: 30px; height: 30px; display: flex; align-items: center; justify-content: center;">
        ${iconHtml}
      </div>
    </div>
  `;

  return L.divIcon({
    className: "custom-pin-icon",
    html: svgIcon,
    iconSize: [40, 60],
    iconAnchor: [20, 60],
    popupAnchor: [0, -60],
  });
};

// Heatmap Layer
function HeatmapLayer({ points, visible }) {
  const map = useMapEvents({});
  useEffect(() => {
    if (!map || !visible) return;
    const heatPoints = points
      .map((p) => p.latitude && p.longitude && [p.latitude, p.longitude, 2])
      .filter(Boolean);
    const heatLayer = L.heatLayer(heatPoints, {
      radius: 30,
      blur: 20,
      maxZoom: 8,
      gradient: { 0.4: "blue", 0.65: "lime", 1: "red" },
    });
    heatLayer.addTo(map);
    return () => map.removeLayer(heatLayer);
  }, [map, points, visible]);

  return null;
}

const ProjectMap = ({ data }) => {
  const [loading, setLoading] = useState(true);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedSiteVisit, setSelectedSiteVisit] = useState(null);
  const [showHeatmap, setShowHeatmap] = useState(false);
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const projectId = localStorage.getItem("currentProjectID");

  const points = data?.data?.activityByLocation?.activityByLocationMap || [];

  useEffect(() => {
    if (points.length > 0) {
      setLoading(false);
    }
  }, [points]);

  const handleMarkerClick = (siteVisitId) => {
    setSelectedSiteVisit(siteVisitId);
    setShowPreviewModal(true);
  };

  const exportMap = async (format) => {
    if (!mapContainerRef.current) return;
    try {
      const canvas = await html2canvas(mapContainerRef.current, {
        useCORS: true,
        allowTaint: true,
        backgroundColor: "#fff",
      });

      const imageData = canvas.toDataURL("image/png");

      if (format === "pdf") {
        const pdf = new jsPDF({ orientation: "landscape" });
        pdf.addImage(imageData, "PNG", 10, 10, 280, 150);
        pdf.save("map-export.pdf");
      } else {
        const mime = format === "jpeg" ? "image/jpeg" : "image/png";
        const link = document.createElement("a");
        link.href = canvas.toDataURL(mime);
        link.download = `map-export.${format}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Export failed:", error);
    }
  };

  const handleExportClick = () => {
    const payload = {
      projectId,
      publicDate: new Date().toISOString(),
    };
    const secret = "K4JW7-RRJM7-VTJJY-JV899-QHVYM";
    const encrypted = CryptoJS.AES.encrypt(
      JSON.stringify(payload),
      secret
    ).toString();
    const url = `/publicMap?map=${encodeURIComponent(encrypted)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <>
      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          alignItems: "center",
          marginBottom: 20,
          gap: 5,
        }}
      >
        {/* <AntdTooltip title="Export Map">
          <Button
            type="default"
            icon={<ExportOutlined />}
            onClick={handleExportClick}
          />
        </AntdTooltip> */}
        <AntdTooltip title="Toggle Heatmap">
          <Button
            type={showHeatmap ? "primary" : "default"}
            icon={<HeatMapOutlined />}
            onClick={() => setShowHeatmap((prev) => !prev)}
          />
        </AntdTooltip>
        <AntdTooltip title="Export as PNG">
          <Button
            icon={<FileImageOutlined />}
            onClick={() => exportMap("png")}
          />
        </AntdTooltip>
        <AntdTooltip title="Export as PDF">
          <Button icon={<FilePdfOutlined />} onClick={() => exportMap("pdf")} />
        </AntdTooltip>
      </div>
      <div
        ref={mapContainerRef}
        style={{
          height: "600px",
          width: "100%",
          position: "relative",
          display: loading ? "flex" : "block",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
 
          <MapContainer
            whenCreated={(mapInstance) => (mapRef.current = mapInstance)}
            minZoom={1}
            center={[20, 0]}
            zoom={2}
            maxBounds={[
              [-90, -180],
              [90, 180],
            ]}
            maxBoundsViscosity={1.0}
          >
            <TileLayer
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
              attribution="&copy; OpenStreetMap contributors"
              crossOrigin="anonymous"
            />

            {!showHeatmap && points.length > 0 && (
              <MarkerClusterGroup chunkedLoading>
                {points.map((item, idx) => (
                  <Marker
                    key={idx}
                    position={[item.latitude || 0, item.longitude || 0]}
                    icon={createCustomIcon(item.projectId)}
                    eventHandlers={{
                      click: () => handleMarkerClick(item.siteVisitId),
                    }}
                  >
                    <LeafletTooltip
                      direction="top"
                      offset={[0, -30]}
                      opacity={1}
                    >
                      {item.siteVisitIdentifier ||
                        item.siteVisitName ||
                        "Site Visit"}
                    </LeafletTooltip>
                  </Marker>
                ))}
              </MarkerClusterGroup>
            )}

            {showHeatmap && (
              <HeatmapLayer points={points} visible={showHeatmap} />
            )}
          </MapContainer>
      
      </div>

      <Preview
        visible={showPreviewModal}
        onClose={() => setShowPreviewModal(false)}
        siteVisitCardId={selectedSiteVisit}
      />
    </>
  );
};

export default ProjectMap;
